/** Live updates of what's on screen: an event stream, and what happens when something on it changes. */
import { useEffect, useRef } from "preact/hooks";
import { API, token } from "../api/client.ts";
import { ignore } from "./ignore.ts";
import { subscribe } from "./sse.ts";

/**
 * Follows the event stream at `path` (under the API's `/v1`) while mounted: `onChange` on every event, and on every
 * reconnection (whatever happened meanwhile), not on the first connection. Opened again when `renew` changes (a
 * stream keeps what its subscriber could see when it opened).
 */
export function useLive(path: string, onChange: () => void, renew: unknown = null): void {
  const changed = useRef(onChange);
  changed.current = onChange;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `renew` is a reconnect trigger.
  useEffect(() => {
    const ctl = new AbortController();
    let opened = false;
    const onOpen = () => {
      if (opened) {
        changed.current();
      }
      opened = true;
    };
    const onEvent = () => changed.current();
    subscribe(`${API}/v1${path}/events`, { token, onEvent, onOpen, signal: ctl.signal }).catch(ignore);
    return () => ctl.abort();
  }, [path, renew]);
}
